import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, extractError } from "../lib/api";
import { PageHeader, Pill, SectionCard } from "../components/Ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Plus, RefreshCw, Trash2 } from "lucide-react";

const NONE = "__none__";

const DEFAULT_CATEGORIES = [
  { value: "PACKING_MATERIAL", label: "Packing material" },
  { value: "CONSUMABLE", label: "Consumable" },
  { value: "SPARES", label: "Spares" },
  { value: "FIXED_ASSET", label: "Fixed asset" },
];

const DEFAULT_UNITS = ["Pcs", "Kgs", "Bags", "Ltrs", "Nos", "Set", "Mtr"];

function money(n) {
  return Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function lineTax(qty, rate, gstRate) {
  const q = Number(qty) || 0;
  const r = Number(rate) || 0;
  const g = Number(gstRate) || 0;
  const taxable = Math.round(q * r * 100) / 100;
  const gst = Math.round(taxable * g) / 100;
  return { taxable, gst, net: Math.round((taxable + gst) * 100) / 100 };
}

function statusTone(s) {
  const u = String(s || "").toUpperCase();
  if (u === "COMPLETED" || u === "POSTED" || u === "APPROVED") return "success";
  if (u === "PARTIALLY_RECEIVED" || u === "DRAFT") return "warning";
  return "default";
}

export default function GeneralProcurement() {
  const [tab, setTab] = useState("orders");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [units, setUnits] = useState(DEFAULT_UNITS);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [openPos, setOpenPos] = useState([]);
  const [grns, setGrns] = useState([]);
  const [pendingGrns, setPendingGrns] = useState([]);
  const [pendingPoPick, setPendingPoPick] = useState({});
  const [stock, setStock] = useState([]);
  const [assets, setAssets] = useState([]);

  // Item form
  const [itemForm, setItemForm] = useState({
    name: "",
    category: "CONSUMABLE",
    sub_category: "",
    hsn_code: "",
    unit: "Pcs",
    reorder_level: "",
    brand_name: "",
    pack_capacity_kg: "",
    default_gst_rate: "18",
  });

  // PO form
  const [poForm, setPoForm] = useState({
    supplier_id: "",
    category: "CONSUMABLE",
    po_date: "",
    remarks: "",
  });
  const [poLines, setPoLines] = useState([
    { key: "l1", item_id: "", quantity: "", unit_price: "", gst_rate: "18" },
  ]);

  // GRN form
  const [grnPoId, setGrnPoId] = useState("");
  const [grnMeta, setGrnMeta] = useState({
    supplier_invoice_no: "",
    supplier_invoice_date: "",
    received_date: "",
    inspected_by: "",
    remarks: "",
  });
  const [grnLines, setGrnLines] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const settled = await Promise.allSettled([
        api.get("/procurement/bootstrap"),
        api.get("/procurement/purchase-orders", { params: { limit: 50 } }),
        api.get("/procurement/grns", { params: { limit: 50 } }),
        api.get("/procurement/grns/pending-po"),
        api.get("/procurement/store-stock"),
        api.get("/procurement/fixed-assets", { params: { limit: 100 } }),
        api.get("/procurement/items"),
      ]);
      const [bootS, poS, grnS, pendingS, stockS, assetS, itemsS] = settled;
      const failed = settled.find((r) => r.status === "rejected");
      if (failed) toast.error(extractError(failed.reason));

      if (bootS.status === "fulfilled") {
        const boot = bootS.value.data || {};
        setCategories(boot.categories?.length ? boot.categories : DEFAULT_CATEGORIES);
        setUnits(boot.units?.length ? boot.units : DEFAULT_UNITS);
        setSuppliers(boot.suppliers || []);
        setItems(boot.items || []);
        setOpenPos(boot.open_pos || []);
      } else {
        setCategories(DEFAULT_CATEGORIES);
      }
      // Prefer dedicated items list if bootstrap had none / failed
      if (itemsS.status === "fulfilled") {
        const rows = itemsS.value.data?.results || [];
        if (rows.length) setItems(rows);
      }
      if (poS.status === "fulfilled") setOrders(poS.value.data?.results || []);
      if (grnS.status === "fulfilled") setGrns(grnS.value.data?.results || []);
      if (pendingS.status === "fulfilled") setPendingGrns(pendingS.value.data?.results || []);
      if (stockS.status === "fulfilled") setStock(stockS.value.data?.results || []);
      if (assetS.status === "fulfilled") setAssets(assetS.value.data?.results || []);
    } catch (err) {
      toast.error(extractError(err));
      setCategories(DEFAULT_CATEGORIES);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const categoryOptions = categories.length ? categories : DEFAULT_CATEGORIES;

  const itemsForCategory = useMemo(
    () => items.filter((it) => it.category === poForm.category),
    [items, poForm.category]
  );

  const poTotals = useMemo(() => {
    let taxable = 0;
    let gst = 0;
    for (const ln of poLines) {
      const it = items.find((x) => String(x.id) === String(ln.item_id));
      const t = lineTax(ln.quantity, ln.unit_price, ln.gst_rate || it?.default_gst_rate || 18);
      taxable += t.taxable;
      gst += t.gst;
    }
    return { taxable, gst, grand: Math.round((taxable + gst) * 100) / 100 };
  }, [poLines, items]);

  const selectedPo = useMemo(
    () => openPos.find((p) => String(p.id) === String(grnPoId)) || orders.find((p) => String(p.id) === String(grnPoId)),
    [openPos, orders, grnPoId]
  );

  useEffect(() => {
    if (!selectedPo) {
      setGrnLines([]);
      return;
    }
    setGrnLines(
      (selectedPo.items || [])
        .filter((ln) => Number(ln.pending_qty) > 0.001)
        .map((ln) => ({
          po_item_id: ln.id,
          item_name: ln.item_name,
          pending_qty: ln.pending_qty,
          unit: ln.unit,
          received_qty: String(ln.pending_qty),
          accepted_qty: String(ln.pending_qty),
          rejected_qty: "0",
        }))
    );
  }, [selectedPo?.id]);

  const createItem = async (e) => {
    e.preventDefault();
    if (!itemForm.name.trim()) return toast.error("Item name required");
    setBusy(true);
    try {
      await api.post("/procurement/items", {
        ...itemForm,
        reorder_level: Number(itemForm.reorder_level) || 0,
        pack_capacity_kg: Number(itemForm.pack_capacity_kg) || 0,
        default_gst_rate: Number(itemForm.default_gst_rate) || 18,
      });
      toast.success("Item created");
      setItemForm((f) => ({ ...f, name: "", sub_category: "", hsn_code: "", brand_name: "" }));
      await load();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  const createPo = async (e) => {
    e.preventDefault();
    if (!poForm.supplier_id) return toast.error("Select supplier");
    const lines = poLines
      .filter((ln) => ln.item_id && Number(ln.quantity) > 0)
      .map((ln) => ({
        item_id: Number(ln.item_id),
        quantity: Number(ln.quantity),
        unit_price: Number(ln.unit_price) || 0,
        gst_rate: Number(ln.gst_rate) || 18,
      }));
    if (!lines.length) return toast.error("Add at least one PO line");
    setBusy(true);
    try {
      const { data } = await api.post("/procurement/purchase-orders", {
        supplier_id: Number(poForm.supplier_id),
        category: poForm.category,
        po_date: poForm.po_date || undefined,
        remarks: poForm.remarks || "",
        auto_approve: true,
        items: lines,
      });
      toast.success(`PO ${data.po_number} created`);
      setPoLines([{ key: `l${Date.now()}`, item_id: "", quantity: "", unit_price: "", gst_rate: "18" }]);
      setPoForm((f) => ({ ...f, remarks: "" }));
      await load();
      setTab("orders");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  const linkPendingGrn = async (grnId) => {
    const poId = pendingPoPick[grnId];
    if (!poId) return toast.error("Select a PO to link");
    setBusy(true);
    try {
      await api.post(`/procurement/grns/${grnId}/link-po`, { po_id: Number(poId) });
      toast.success("PO linked — purchase journal posted");
      setPendingPoPick((prev) => {
        const next = { ...prev };
        delete next[grnId];
        return next;
      });
      await load();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitGrn = async (e) => {
    e.preventDefault();
    if (!grnPoId) return toast.error("Select a PO");
    const lines = grnLines
      .filter((ln) => Number(ln.received_qty) > 0)
      .map((ln) => ({
        po_item_id: ln.po_item_id,
        received_qty: Number(ln.received_qty),
        accepted_qty: Number(ln.accepted_qty),
        rejected_qty: Number(ln.rejected_qty) || 0,
      }));
    if (!lines.length) return toast.error("Enter received quantities");
    setBusy(true);
    try {
      const { data } = await api.post("/procurement/grn", {
        po_id: Number(grnPoId),
        ...grnMeta,
        lines,
        create_voucher: true,
      });
      toast.success(`GRN ${data.grn_number} posted`);
      setGrnPoId("");
      setGrnLines([]);
      await load();
      setTab("grn");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading && !orders.length && !items.length) {
    return (
      <div className="p-6 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading procurement…
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 md:p-6" data-testid="general-procurement-page">
      <PageHeader
        eyebrow="Purchase · Stores"
        title="General procurement"
        description="Packing material, consumables, spares & fixed assets — PO → GRN → store / asset register. Store tablet intake is at Store keeper GRN."
        actions={
          <Button variant="outline" size="sm" onClick={load} disabled={busy}>
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        }
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-card border border-border p-1 h-auto flex-wrap">
          <TabsTrigger value="orders" className="text-xs sm:text-sm">Purchase orders</TabsTrigger>
          <TabsTrigger value="grn" className="text-xs sm:text-sm">Goods receipt (GRN)</TabsTrigger>
          <TabsTrigger value="stock" className="text-xs sm:text-sm">Store stock</TabsTrigger>
          <TabsTrigger value="assets" className="text-xs sm:text-sm">Fixed assets</TabsTrigger>
          <TabsTrigger value="masters" className="text-xs sm:text-sm">Item master</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-4 space-y-4">
          <SectionCard title="Create purchase order" testId="gp-po-form">
            <form onSubmit={createPo} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Category</Label>
                  <Select
                    value={poForm.category || "CONSUMABLE"}
                    onValueChange={(v) => {
                      setPoForm((f) => ({ ...f, category: v }));
                      setPoLines([{ key: `l${Date.now()}`, item_id: "", quantity: "", unit_price: "", gst_rate: "18" }]);
                    }}
                  >
                    <SelectTrigger data-testid="gp-po-category">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categoryOptions.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Supplier</Label>
                  <Select
                    value={poForm.supplier_id || NONE}
                    onValueChange={(v) => setPoForm((f) => ({ ...f, supplier_id: v === NONE ? "" : v }))}
                  >
                    <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>—</SelectItem>
                      {suppliers.map((s) => (
                        <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">PO date</Label>
                  <Input
                    type="date"
                    value={poForm.po_date}
                    onChange={(e) => setPoForm((f) => ({ ...f, po_date: e.target.value }))}
                  />
                </div>
              </div>

              <div className="overflow-x-auto border border-border rounded-md">
                <table className="w-full text-xs min-w-[720px]">
                  <thead className="bg-muted/50 text-[10px] uppercase text-muted-foreground">
                    <tr>
                      <th className="text-left py-2 px-2">Item</th>
                      <th className="text-right py-2 px-2">Qty</th>
                      <th className="text-right py-2 px-2">Rate</th>
                      <th className="text-right py-2 px-2">GST %</th>
                      <th className="text-right py-2 px-2">Taxable</th>
                      <th className="text-right py-2 px-2">GST</th>
                      <th className="text-right py-2 px-2">Net</th>
                      <th className="w-10" />
                    </tr>
                  </thead>
                  <tbody>
                    {poLines.map((ln, idx) => {
                      const it = items.find((x) => String(x.id) === String(ln.item_id));
                      const t = lineTax(ln.quantity, ln.unit_price, ln.gst_rate || it?.default_gst_rate || 18);
                      return (
                        <tr key={ln.key} className="border-t border-border/60">
                          <td className="py-1.5 px-2 min-w-[180px]">
                            <Select
                              value={ln.item_id || NONE}
                              onValueChange={(v) => {
                                const item = items.find((x) => String(x.id) === v);
                                setPoLines((rows) =>
                                  rows.map((r, i) =>
                                    i === idx
                                      ? {
                                          ...r,
                                          item_id: v === NONE ? "" : v,
                                          gst_rate: item ? String(item.default_gst_rate || 18) : r.gst_rate,
                                        }
                                      : r
                                  )
                                );
                              }}
                            >
                              <SelectTrigger className="h-8"><SelectValue placeholder="Item" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value={NONE}>— Select item —</SelectItem>
                                {itemsForCategory.map((it) => (
                                  <SelectItem key={it.id} value={String(it.id)}>
                                    {it.name} ({it.code})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {!itemsForCategory.length ? (
                              <p className="text-[10px] text-amber-700 dark:text-amber-400 mt-1">
                                No items for this category. Add them under{" "}
                                <button
                                  type="button"
                                  className="underline"
                                  onClick={() => setTab("masters")}
                                >
                                  Item master
                                </button>
                                .
                              </p>
                            ) : null}
                          </td>
                          <td className="py-1.5 px-2">
                            <Input
                              className="h-8 text-right"
                              type="number"
                              step="0.001"
                              value={ln.quantity}
                              onChange={(e) =>
                                setPoLines((rows) =>
                                  rows.map((r, i) => (i === idx ? { ...r, quantity: e.target.value } : r))
                                )
                              }
                            />
                          </td>
                          <td className="py-1.5 px-2">
                            <Input
                              className="h-8 text-right"
                              type="number"
                              step="0.01"
                              value={ln.unit_price}
                              onChange={(e) =>
                                setPoLines((rows) =>
                                  rows.map((r, i) => (i === idx ? { ...r, unit_price: e.target.value } : r))
                                )
                              }
                            />
                          </td>
                          <td className="py-1.5 px-2">
                            <Input
                              className="h-8 text-right"
                              type="number"
                              step="0.01"
                              value={ln.gst_rate}
                              onChange={(e) =>
                                setPoLines((rows) =>
                                  rows.map((r, i) => (i === idx ? { ...r, gst_rate: e.target.value } : r))
                                )
                              }
                            />
                          </td>
                          <td className="py-1.5 px-2 text-right tabular">{money(t.taxable)}</td>
                          <td className="py-1.5 px-2 text-right tabular">{money(t.gst)}</td>
                          <td className="py-1.5 px-2 text-right tabular font-medium">{money(t.net)}</td>
                          <td className="py-1.5 px-1">
                            {poLines.length > 1 ? (
                              <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                onClick={() => setPoLines((rows) => rows.filter((_, i) => i !== idx))}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setPoLines((rows) => [
                      ...rows,
                      { key: `l${Date.now()}`, item_id: "", quantity: "", unit_price: "", gst_rate: "18" },
                    ])
                  }
                >
                  <Plus className="h-4 w-4 mr-1" /> Add line
                </Button>
                <div className="text-sm text-foreground">
                  Taxable ₹{money(poTotals.taxable)} · GST ₹{money(poTotals.gst)} ·{" "}
                  <span className="font-semibold">Grand ₹{money(poTotals.grand)}</span>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Remarks</Label>
                <Textarea
                  rows={2}
                  value={poForm.remarks}
                  onChange={(e) => setPoForm((f) => ({ ...f, remarks: e.target.value }))}
                />
              </div>
              <Button type="submit" disabled={busy} className="bg-bansal-sage hover:bg-bansal-sageDark text-white">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Create &amp; approve PO
              </Button>
            </form>
          </SectionCard>

          <SectionCard title={`Orders (${orders.length})`} testId="gp-po-list">
            {!orders.length ? (
              <p className="text-sm text-muted-foreground">No purchase orders yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="text-[10px] uppercase text-muted-foreground border-b border-border">
                    <tr>
                      <th className="text-left py-2 px-2">PO</th>
                      <th className="text-left py-2 px-2">Supplier</th>
                      <th className="text-left py-2 px-2">Category</th>
                      <th className="text-left py-2 px-2">Status</th>
                      <th className="text-right py-2 px-2">Grand</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((po) => (
                      <tr key={po.id} className="border-b border-border/50">
                        <td className="py-2 px-2 font-mono text-xs">{po.po_number}</td>
                        <td className="py-2 px-2">{po.supplier_name}</td>
                        <td className="py-2 px-2 text-xs text-muted-foreground">{(po.category || "").replaceAll("_", " ")}</td>
                        <td className="py-2 px-2"><Pill tone={statusTone(po.status)}>{po.status}</Pill></td>
                        <td className="py-2 px-2 text-right tabular">₹{money(po.grand_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </TabsContent>

        <TabsContent value="grn" className="mt-4 space-y-4">
          <SectionCard title="Receive against PO" testId="gp-grn-form">
            <form onSubmit={submitGrn} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1 md:col-span-2">
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Open PO</Label>
                  <Select value={grnPoId || NONE} onValueChange={(v) => setGrnPoId(v === NONE ? "" : v)}>
                    <SelectTrigger><SelectValue placeholder="Select PO" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>—</SelectItem>
                      {openPos.map((p) => (
                        <SelectItem key={p.id} value={String(p.id)}>
                          {p.po_number} · {p.supplier_name} · {p.category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Supplier invoice no</Label>
                  <Input
                    value={grnMeta.supplier_invoice_no}
                    onChange={(e) => setGrnMeta((f) => ({ ...f, supplier_invoice_no: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Invoice date</Label>
                  <Input
                    type="date"
                    value={grnMeta.supplier_invoice_date}
                    onChange={(e) => setGrnMeta((f) => ({ ...f, supplier_invoice_date: e.target.value }))}
                  />
                </div>
              </div>

              {grnLines.length > 0 ? (
                <div className="overflow-x-auto border border-border rounded-md">
                  <table className="w-full text-xs min-w-[640px]">
                    <thead className="bg-muted/50 text-[10px] uppercase text-muted-foreground">
                      <tr>
                        <th className="text-left py-2 px-2">Item</th>
                        <th className="text-right py-2 px-2">Pending</th>
                        <th className="text-right py-2 px-2">Received</th>
                        <th className="text-right py-2 px-2">Accepted</th>
                        <th className="text-right py-2 px-2">Rejected</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grnLines.map((ln, idx) => (
                        <tr key={ln.po_item_id} className="border-t border-border/60">
                          <td className="py-1.5 px-2">{ln.item_name} <span className="text-muted-foreground">({ln.unit})</span></td>
                          <td className="py-1.5 px-2 text-right tabular">{ln.pending_qty}</td>
                          <td className="py-1.5 px-2">
                            <Input
                              className="h-8 text-right"
                              type="number"
                              step="0.001"
                              value={ln.received_qty}
                              onChange={(e) =>
                                setGrnLines((rows) =>
                                  rows.map((r, i) => (i === idx ? { ...r, received_qty: e.target.value } : r))
                                )
                              }
                            />
                          </td>
                          <td className="py-1.5 px-2">
                            <Input
                              className="h-8 text-right"
                              type="number"
                              step="0.001"
                              value={ln.accepted_qty}
                              onChange={(e) =>
                                setGrnLines((rows) =>
                                  rows.map((r, i) => (i === idx ? { ...r, accepted_qty: e.target.value } : r))
                                )
                              }
                            />
                          </td>
                          <td className="py-1.5 px-2">
                            <Input
                              className="h-8 text-right"
                              type="number"
                              step="0.001"
                              value={ln.rejected_qty}
                              onChange={(e) =>
                                setGrnLines((rows) =>
                                  rows.map((r, i) => (i === idx ? { ...r, rejected_qty: e.target.value } : r))
                                )
                              }
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Select an open PO with pending qty to inspect receipt.</p>
              )}

              <Button type="submit" disabled={busy || !grnLines.length} className="bg-bansal-sage hover:bg-bansal-sageDark text-white">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Post GRN &amp; update stock
              </Button>
            </form>
          </SectionCard>

          {pendingGrns.length > 0 && (
            <SectionCard title={`Ad-hoc GRNs awaiting PO (${pendingGrns.length})`}>
              <p className="text-xs text-muted-foreground mb-3">
                Store posted these receipts without a PO. Link an approved PO with matching items to post purchase valuation.
              </p>
              <div className="space-y-3">
                {pendingGrns.map((g) => (
                  <div key={g.id} className="rounded-xl border border-border p-3 flex flex-wrap items-end gap-3">
                    <div className="min-w-[120px]">
                      <p className="font-mono text-sm">{g.grn_number}</p>
                      <p className="text-xs text-muted-foreground">{g.received_date} · {(g.lines || []).length} line(s)</p>
                    </div>
                    <div className="flex-1 min-w-[200px]">
                      <Select
                        value={pendingPoPick[g.id] || NONE}
                        onValueChange={(v) =>
                          setPendingPoPick((prev) => ({ ...prev, [g.id]: v === NONE ? "" : v }))
                        }
                      >
                        <SelectTrigger><SelectValue placeholder="Select PO…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE}>Select PO…</SelectItem>
                          {openPos.map((p) => (
                            <SelectItem key={p.id} value={String(p.id)}>
                              {p.po_number} · {p.supplier_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      className="bg-bansal-sage hover:bg-bansal-sageDark text-white"
                      disabled={busy || !pendingPoPick[g.id]}
                      onClick={() => linkPendingGrn(g.id)}
                    >
                      Link PO
                    </Button>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          <SectionCard title={`Recent GRNs (${grns.length})`}>
            {!grns.length ? (
              <p className="text-sm text-muted-foreground">No GRNs yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[560px]">
                  <thead className="text-[10px] uppercase text-muted-foreground border-b border-border">
                    <tr>
                      <th className="text-left py-2 px-2">GRN</th>
                      <th className="text-left py-2 px-2">PO</th>
                      <th className="text-left py-2 px-2">Supplier</th>
                      <th className="text-left py-2 px-2">Date</th>
                      <th className="text-left py-2 px-2">Invoice</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grns.map((g) => (
                      <tr key={g.id} className="border-b border-border/50">
                        <td className="py-2 px-2 font-mono text-xs">{g.grn_number}</td>
                        <td className="py-2 px-2 font-mono text-xs">{g.po_number || (g.po_linkage_status === "PENDING" ? "PO pending" : "—")}</td>
                        <td className="py-2 px-2">{g.supplier_name}</td>
                        <td className="py-2 px-2 tabular text-xs">{g.received_date}</td>
                        <td className="py-2 px-2 text-xs">{g.supplier_invoice_no || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </TabsContent>

        <TabsContent value="stock" className="mt-4 space-y-4">
          <SectionCard title="Store stock ledger" testId="gp-store-stock">
            <p className="text-xs text-muted-foreground mb-3">
              Packing materials, consumables and spares credited on GRN acceptance. Fixed assets go to the asset register.
            </p>
            {!stock.length ? (
              <p className="text-sm text-muted-foreground">No store stock yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="text-[10px] uppercase text-muted-foreground border-b border-border">
                    <tr>
                      <th className="text-left py-2 px-2">Item</th>
                      <th className="text-left py-2 px-2">Category</th>
                      <th className="text-left py-2 px-2">Location</th>
                      <th className="text-right py-2 px-2">Qty</th>
                      <th className="text-left py-2 px-2">Unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stock.map((s) => (
                      <tr key={s.id} className="border-b border-border/50">
                        <td className="py-2 px-2">
                          <span className="font-medium">{s.item_name}</span>
                          <span className="block text-[10px] font-mono text-muted-foreground">{s.item_code}</span>
                        </td>
                        <td className="py-2 px-2 text-xs">{(s.category || "").replaceAll("_", " ")}</td>
                        <td className="py-2 px-2 text-xs">{s.location}</td>
                        <td className={`py-2 px-2 text-right tabular ${s.below_reorder ? "text-red-600" : ""}`}>
                          {s.qty_on_hand}
                        </td>
                        <td className="py-2 px-2 text-xs">{s.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </TabsContent>

        <TabsContent value="assets" className="mt-4 space-y-4">
          <SectionCard title="Fixed asset register" testId="gp-fixed-assets">
            {!assets.length ? (
              <p className="text-sm text-muted-foreground">No fixed assets capitalized from GRN yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                  <thead className="text-[10px] uppercase text-muted-foreground border-b border-border">
                    <tr>
                      <th className="text-left py-2 px-2">Code</th>
                      <th className="text-left py-2 px-2">Name</th>
                      <th className="text-left py-2 px-2">Supplier</th>
                      <th className="text-left py-2 px-2">Serial</th>
                      <th className="text-right py-2 px-2">Cost</th>
                      <th className="text-left py-2 px-2">Dept</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assets.map((a) => (
                      <tr key={a.id} className="border-b border-border/50">
                        <td className="py-2 px-2 font-mono text-xs">{a.asset_code}</td>
                        <td className="py-2 px-2">{a.asset_name}</td>
                        <td className="py-2 px-2 text-xs">{a.supplier_name || "—"}</td>
                        <td className="py-2 px-2 text-xs">{a.serial_number || "—"}</td>
                        <td className="py-2 px-2 text-right tabular">₹{money(a.cost_value)}</td>
                        <td className="py-2 px-2 text-xs">{a.location_dept || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </TabsContent>

        <TabsContent value="masters" className="mt-4 space-y-4">
          <SectionCard title="Add general item" testId="gp-item-form">
            <form onSubmit={createItem} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div className="space-y-1 md:col-span-2">
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Name</Label>
                <Input value={itemForm.name} onChange={(e) => setItemForm((f) => ({ ...f, name: e.target.value }))} required />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Category</Label>
                <Select value={itemForm.category} onValueChange={(v) => setItemForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {categoryOptions.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Sub-category</Label>
                <Input value={itemForm.sub_category} onChange={(e) => setItemForm((f) => ({ ...f, sub_category: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">HSN</Label>
                <Input value={itemForm.hsn_code} onChange={(e) => setItemForm((f) => ({ ...f, hsn_code: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Unit</Label>
                <Select value={itemForm.unit} onValueChange={(v) => setItemForm((f) => ({ ...f, unit: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {units.map((u) => (
                      <SelectItem key={u} value={u}>{u}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">GST %</Label>
                <Input
                  type="number"
                  value={itemForm.default_gst_rate}
                  onChange={(e) => setItemForm((f) => ({ ...f, default_gst_rate: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Brand (packing)</Label>
                <Input value={itemForm.brand_name} onChange={(e) => setItemForm((f) => ({ ...f, brand_name: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Pack capacity kg</Label>
                <Input
                  type="number"
                  value={itemForm.pack_capacity_kg}
                  onChange={(e) => setItemForm((f) => ({ ...f, pack_capacity_kg: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Reorder level</Label>
                <Input
                  type="number"
                  value={itemForm.reorder_level}
                  onChange={(e) => setItemForm((f) => ({ ...f, reorder_level: e.target.value }))}
                />
              </div>
              <Button type="submit" disabled={busy}>Save item</Button>
            </form>
          </SectionCard>

          <SectionCard title={`Items (${items.length})`}>
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-sm min-w-[560px]">
                <thead className="text-[10px] uppercase text-muted-foreground border-b border-border sticky top-0 bg-card">
                  <tr>
                    <th className="text-left py-2 px-2">Code</th>
                    <th className="text-left py-2 px-2">Name</th>
                    <th className="text-left py-2 px-2">Category</th>
                    <th className="text-left py-2 px-2">Unit</th>
                    <th className="text-right py-2 px-2">GST%</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id} className="border-b border-border/50">
                      <td className="py-1.5 px-2 font-mono text-xs">{it.code}</td>
                      <td className="py-1.5 px-2">{it.name}</td>
                      <td className="py-1.5 px-2 text-xs">{(it.category || "").replaceAll("_", " ")}</td>
                      <td className="py-1.5 px-2 text-xs">{it.unit}</td>
                      <td className="py-1.5 px-2 text-right tabular">{it.default_gst_rate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
